/**
 * Admin User Management
 * View, search, and manage platform users
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Shield, UserCheck, UserX, Ban, BadgeCheck,
  Loader2, RefreshCw, Filter, ChevronDown, Mail, Building2, Phone,
  MailCheck, MailX, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const AdminUserManagement = () => {
  const { apiCall, formatDate } = useAdmin();
  
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all, private, business, admin, banned
  const [showFilters, setShowFilters] = useState(false);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [showBanModal, setShowBanModal] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 20;
  
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(searchQuery && { search: searchQuery }),
        ...(filter === 'banned' && { status: 'banned' }),
        ...(filter === 'unverified_email' && { status: 'unverified_email' }),
        ...(filter !== 'all' && filter !== 'banned' && filter !== 'unverified_email' && { account_type: filter })
      });
      
      const [usersRes, adminsRes] = await Promise.all([
        apiCall('get', `/api/admin/users?${params}`),
        apiCall('get', '/api/admin/users/admins')
      ]);
      
      setUsers(usersRes.users || usersRes || []);
      setTotalUsers(usersRes.total || usersRes.length || 0);
      setAdminUsers(adminsRes.admins || adminsRes || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, searchQuery, filter]);
  
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter, searchQuery]);
  
  // User actions
  const handleBanUser = async (userId, userName) => {
    if (!banReason.trim()) {
      toast.error('Please provide a reason for banning');
      return;
    }
    try {
      setActionLoading(userId);
      await apiCall('put', `/api/admin/users/${userId}/ban?reason=${encodeURIComponent(banReason)}`);
      toast.success(`${userName} has been banned`);
      setBanReason('');
      setShowBanModal(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to ban user:', error);
      toast.error('Failed to ban user');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };
  
  const handleUnbanUser = async (userId, userName) => {
    try {
      setActionLoading(userId);
      await apiCall('put', `/api/admin/users/${userId}/unban`);
      toast.success(`${userName} has been unbanned`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to unban user:', error);
      toast.error('Failed to unban user');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleMakeAdmin = async (userId, userName) => {
    try {
      setActionLoading(userId);
      await apiCall('post', `/api/admin/users/${userId}/make-admin`);
      toast.success(`${userName} is now an admin`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to make admin:', error);
      toast.error('Failed to make user admin');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };
  
  const handleRemoveAdmin = async (userId, userName) => {
    try {
      setActionLoading(userId);
      await apiCall('post', `/api/admin/users/${userId}/remove-admin`);
      toast.success(`${userName} is no longer an admin`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to remove admin:', error);
      toast.error('Failed to remove admin status');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };
  
  const handleResendVerification = async (userId, userEmail) => {
    try {
      setActionLoading(`resend-${userId}`);
      await apiCall('post', `/api/admin/users/${userId}/resend-verification`);
      toast.success(`Verification email sent to ${userEmail}`);
    } catch (error) {
      console.error('Failed to resend verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleManualVerify = async (userId, userName) => {
    try {
      setActionLoading(`verify-${userId}`);
      await apiCall('post', `/api/admin/users/${userId}/manual-verify`);
      toast.success(`Email verified for ${userName}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to verify email:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify email');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };
  
  const totalPages = Math.ceil(totalUsers / limit);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            User Management
          </h2>
          <p className="text-sm text-muted-foreground">
            {totalUsers} total users • {adminUsers.length} admins
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-border rounded-lg bg-background"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors ${showFilters ? 'bg-primary text-primary-foreground' : 'border-border hover:bg-secondary'}`}
          >
            <Filter className="w-4 h-4" />
          </button>
          
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 p-4 bg-secondary/30 rounded-lg">
          {[
            { id: 'all', label: 'All Users' },
            { id: 'private', label: 'Private' },
            { id: 'business', label: 'Business' },
            { id: 'admin', label: 'Admins' },
            { id: 'banned', label: 'Banned' },
            { id: 'unverified_email', label: 'Unverified Email', icon: MailX }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1.5 ${
                filter === f.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card border border-border hover:bg-secondary'
              }`}
            >
              {f.icon && <f.icon className="w-3.5 h-3.5" />}
              {f.label}
            </button>
          ))}
        </div>
      )}
      
      {/* Admins Section */}
      {filter === 'all' && adminUsers.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <h3 className="font-semibold flex items-center gap-2 text-amber-600 mb-3">
            <Shield className="w-4 h-4" />
            Platform Admins ({adminUsers.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {adminUsers.map(admin => (
              <div 
                key={admin.id}
                className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <div className="text-sm font-medium">{admin.name}</div>
                  <div className="text-xs text-muted-foreground">{admin.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium hidden xl:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 text-sm font-medium hidden md:table-cell">Joined</th>
                  <th className="text-left px-4 py-3 text-sm font-medium hidden lg:table-cell">Listings</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-secondary/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center overflow-hidden">
                          {user.avatar ? (
                            <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg font-medium">{user.name?.[0]?.toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {user.name}
                            {user.is_admin && <Shield className="w-3 h-3 text-amber-500" />}
                            {user.is_verified && <BadgeCheck className="w-3 h-3 text-green-500" />}
                          </div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        user.account_type === 'business' 
                          ? 'bg-blue-500/20 text-blue-500' 
                          : user.account_type === 'admin'
                          ? 'bg-amber-500/20 text-amber-500'
                          : 'bg-gray-500/20 text-gray-500'
                      }`}>
                        {user.account_type === 'business' && <Building2 className="w-3 h-3" />}
                        {user.account_type || 'private'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <div className="space-y-1">
                        {user.phone && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3 text-green-500" />
                            <span>{user.phone}</span>
                            {user.hide_phone && (
                              <span className="text-[10px] bg-amber-500/20 text-amber-500 px-1 rounded">hidden</span>
                            )}
                          </div>
                        )}
                        {user.email && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Mail className="w-3 h-3 text-blue-500" />
                            <span className="truncate max-w-[140px]">{user.email}</span>
                            {user.hide_email && (
                              <span className="text-[10px] bg-amber-500/20 text-amber-500 px-1 rounded">hidden</span>
                            )}
                          </div>
                        )}
                        {!user.phone && !user.email && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="font-medium">{user.listings_count || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {user.is_banned ? (
                          <div className="group relative">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-500 cursor-help">
                              <Ban className="w-3 h-3" />
                              Banned
                            </span>
                            {user.ban_reason && (
                              <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-popover border border-border rounded-lg shadow-lg text-xs">
                                <p className="font-semibold text-red-500 mb-1">Ban Reason:</p>
                                <p className="text-muted-foreground">{user.ban_reason}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-500">
                            Active
                          </span>
                        )}
                        {/* Email verification status */}
                        {!user.email_verified && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-500">
                            <MailX className="w-3 h-3" />
                            Unverified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {actionLoading === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            {user.is_banned ? (
                              <button
                                onClick={() => handleUnbanUser(user.id, user.name)}
                                className="p-2 rounded-lg text-green-500 hover:bg-green-500/20 transition-colors"
                                title="Unban"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setConfirmAction({ type: 'ban', user })}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-500/20 transition-colors"
                                title="Ban"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            
                            {user.is_admin ? (
                              <button
                                onClick={() => setConfirmAction({ type: 'remove-admin', user })}
                                className="p-2 rounded-lg text-amber-500 hover:bg-amber-500/20 transition-colors"
                                title="Remove Admin"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setConfirmAction({ type: 'make-admin', user })}
                                className="p-2 rounded-lg text-blue-500 hover:bg-blue-500/20 transition-colors"
                                title="Make Admin"
                              >
                                <Shield className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Email verification actions */}
                            {!user.email_verified && (
                              <>
                                <button
                                  onClick={() => handleResendVerification(user.id, user.email)}
                                  disabled={actionLoading === `resend-${user.id}`}
                                  className="p-2 rounded-lg text-blue-500 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                                  title="Resend Verification Email"
                                >
                                  {actionLoading === `resend-${user.id}` ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => setConfirmAction({ type: 'manual-verify', user })}
                                  disabled={actionLoading === `verify-${user.id}`}
                                  className="p-2 rounded-lg text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                                  title="Manually Verify Email"
                                >
                                  {actionLoading === `verify-${user.id}` ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <MailCheck className="w-4 h-4" />
                                  )}
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalUsers)} of {totalUsers}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border border-border hover:bg-secondary disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border border-border hover:bg-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => { setConfirmAction(null); setBanReason(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={
              confirmAction?.type === 'ban' ? 'text-red-500' :
              confirmAction?.type === 'make-admin' ? 'text-blue-500' :
              confirmAction?.type === 'manual-verify' ? 'text-green-500' :
              'text-amber-500'
            }>
              {confirmAction?.type === 'ban' && 'Ban User'}
              {confirmAction?.type === 'make-admin' && 'Make Admin'}
              {confirmAction?.type === 'remove-admin' && 'Remove Admin'}
              {confirmAction?.type === 'manual-verify' && 'Manually Verify Email'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'ban' && (
                `Are you sure you want to ban ${confirmAction?.user?.name}? They will not be able to access the platform.`
              )}
              {confirmAction?.type === 'make-admin' && (
                `Are you sure you want to make ${confirmAction?.user?.name} an admin? They will have full access to the admin panel.`
              )}
              {confirmAction?.type === 'remove-admin' && (
                `Are you sure you want to remove admin privileges from ${confirmAction?.user?.name}?`
              )}
              {confirmAction?.type === 'manual-verify' && (
                `Are you sure you want to manually verify the email for ${confirmAction?.user?.name} (${confirmAction?.user?.email})? This should only be used when the verification email cannot be delivered.`
              )}
            </DialogDescription>
          </DialogHeader>
          
          {/* Ban Reason Input */}
          {confirmAction?.type === 'ban' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Reason for ban <span className="text-red-500">*</span>
              </label>
              <textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Explain why this user is being banned..."
                rows={3}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500/50"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The user will see this reason when they try to use their account.
              </p>
            </div>
          )}
          
          {/* Manual Verify Warning */}
          {confirmAction?.type === 'manual-verify' && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                <strong>Warning:</strong> Manually verifying skips email confirmation. Only use this when:
              </p>
              <ul className="text-sm text-amber-600/80 dark:text-amber-400/80 mt-2 ml-4 list-disc">
                <li>Email delivery is confirmed failing</li>
                <li>User has verified identity through other means</li>
              </ul>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => { setConfirmAction(null); setBanReason(''); }}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (confirmAction?.type === 'ban') {
                  handleBanUser(confirmAction.user.id, confirmAction.user.name);
                } else if (confirmAction?.type === 'make-admin') {
                  handleMakeAdmin(confirmAction.user.id, confirmAction.user.name);
                } else if (confirmAction?.type === 'remove-admin') {
                  handleRemoveAdmin(confirmAction.user.id, confirmAction.user.name);
                } else if (confirmAction?.type === 'manual-verify') {
                  handleManualVerify(confirmAction.user.id, confirmAction.user.name);
                }
              }}
              disabled={confirmAction?.type === 'ban' && !banReason.trim()}
              className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                confirmAction?.type === 'ban' ? 'bg-red-500 hover:bg-red-600' :
                confirmAction?.type === 'make-admin' ? 'bg-blue-500 hover:bg-blue-600' :
                confirmAction?.type === 'manual-verify' ? 'bg-green-500 hover:bg-green-600' :
                'bg-amber-500 hover:bg-amber-600'
              }`}
            >
              Confirm
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserManagement;
